import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import './MonitoredIndividualSummary.css';
import DateIcon from '../DateIcon/DateIcon';
import LargeNumberIcon from '../LargeNumberIcon/LargeNumberIcon';
import { format, parseISO } from 'date-fns';
import baseApi from '../../common/api/base';
import SurveyModal from '../SurveyModal/SurveyModal';
import DefaultMonitoredIndividualIcon from '../DefaultMonitoredIndividualIcon/DefaultMonitoredIndividualIcon';
import MonitoredIndividualImage from '../MonitoredIndividualImage/MonitoredIndividualImage';
import CardContainer from '../CardContainer/CardContainer';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { CheckinContext } from '../../contexts/CheckinContext';
import logComponent from '../../common/logComponent';

export default function MonitoredIndividualSummary({ close: propsClose }) {
  const environmentContext = useContext(EnvironmentContext);
  const [exiting, setExiting] = useState(false);
  const [visibleDateIndex, setVisibleDateIndex] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(false);
  const [data, setData] = useState(null);
  const [checkInInProgress, setCheckInInProgress] = useState(null);
  const checkinContext = useContext(CheckinContext);
  const { hasToken, selectedMonitoredIndividual, organization, checkinSurvey } = environmentContext;
  const [displayCheckInSurvey, setDisplayCheckInSurvey] = useState(checkinSurvey);

  if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary rendered'); }


  useEffect(() => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary useEffect [environmentContext.selectedMonitoredIndividual]'); }
    loadData();
  }, [environmentContext.selectedMonitoredIndividual])

  useEffect(() => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary useEffect [environmentContext.checkinSurvey]'); }
    setDisplayCheckInSurvey(environmentContext.checkinSurvey);
  }, [environmentContext.checkinSurvey])

  const loadData = () => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary loadData'); }
    const { selectedMonitoredIndividual } = environmentContext;

    setVisibleDateIndex(0);
    setData(null);
    setDataLoading(true);
    setDataError(false);


    baseApi.MonitoredIndividual.GetSummaryForPastDays(selectedMonitoredIndividual.monitoredIndividualId, 7)
      .then(
        result => {
          let prevData = (data ? data : {});
          setData(Object.assign(prevData, result.data));
          setDataLoading(false);
          setDataError(false);
        },
        error => {
          setDataLoading(false);
          setDataError(true);
        }
      )

    window.scrollTo(0, 0);
  }

  const handleDailyNoteIconClick = (e) => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary handleDailyNoteIconClick'); }
    const { noteIndex } = e.currentTarget.dataset;

    if (noteIndex) selectDate(parseInt(noteIndex, 10));
  }

  const handleAnimationEnd = (e) => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary handleAnimationEnd'); }
    if (e.target.matches('.Monitored-Individual-Summary--exiting')) propsClose();
  }

  const selectDate = (newDateIndex) => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary selectDate'); }
    setVisibleDateIndex(newDateIndex);
  }

  const getDailyNoteSummary = () => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary getDailyNoteSummary'); }
    const numberOfNotes = data.noteDays[visibleDateIndex].noteCount;

    if (numberOfNotes === 1) {
      return <p>There is <span className="bold">1</span> check in note available for this day.</p>;
    } else {
      return <p>There are <span className="bold">{numberOfNotes}</span> check in notes available for this day.</p>;
    }
  }

  const getNotesCount = () => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary getNotesCount'); }
    if (!data || !data.noteDays) return 0;

    return data.noteDays.reduce((prev, curr) => {
      return prev + curr.notes.length;
    }, 0);
  }

  const close = () => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary close'); }
    setExiting(true);
  }

  const startCheckIn = () => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary startCheckIn'); }
    setCheckInInProgress(true);
    checkinContext.startCheckin();
  }

  const endCheckIn = () => {
    if (logComponent('MonitoredIndividualSummary')) { console.debug('MonitoredIndividualSummary endCheckIn'); }
    setCheckInInProgress(false);
    checkinContext.endCheckin();
    loadData();
  }

  return (
    <div
      className={'Monitored-Individual-Summary' + (exiting ? ' Monitored-Individual-Summary--exiting' : '')}
      onAnimationEnd={handleAnimationEnd}>
      <div className="Monitored-Individual-Summary__main">
        <p className="Monitored-Individual-Summary__heading">Individual Summary</p>
        {
          data &&
          <div className="Monitored-Individual-Summary__content">
            <div className="Monitored-Individual-Summary__top-row">
              <div className="Monitored-Individual-Summary__bio Monitored-Individual-Summary__top-row-item">
                <div className="Monitored-Individual-Summary__monitoredindividual-info">
                  <div className="Monitored-Individual-Summary__monitoredindividual-image">
                      <MonitoredIndividualImage monitoredIndividual={selectedMonitoredIndividual} />
                      
                  </div>
                  <div className="Monitored-Individual-Summary__monitoredindividual-text">
                    <p className="Monitored-Individual-Summary__monitoredindividual-name">{`${selectedMonitoredIndividual.legalFirstName} ${selectedMonitoredIndividual.lastName}`}</p>
                    <p className="Monitored-Individual-Summary__monitoredindividual-units">
                      {
                        selectedMonitoredIndividual.monitoredIndividualUnits != null &&
                        selectedMonitoredIndividual.monitoredIndividualUnits.filter(item => !item.endOn).map((individualUnit, index) =>
                          <span key={individualUnit.unitId}>{(index > 0 ? ', ' : '')}<Link className="Monitored-Individual-Summary__monitoredindividual-unit-link" to={`/unit/${individualUnit.unitId}`}>{`${individualUnit.unit.name}`}</Link> </span>
                        )
                      }
                    </p>
                  </div>
                </div>
                <div className="Monitored-Individual-Summary__facility-info">
                  {organization.name}
                </div>
                <div className="Monitored-Individual-Summary__check-in-area--md">
                  <button
                    className="Monitored-Individual-Summary__check-in-button"
                    onClick={startCheckIn}>Check in
                  </button>
                </div>
              </div>
              <div className="Monitored-Individual-Summary__past-week Monitored-Individual-Summary__top-row-item">
                <p className="Monitored-Individual-Summary__past-week-heading">This past week</p>
                <div className="Monitored-Individual-Summary__past-week-content">
                  <LargeNumberIcon number={data.checkInCount} text="Check-ins" />
                  <LargeNumberIcon number={data.noteCount} text="Notes" />
                  <LargeNumberIcon number={data.alarmsCount} text="Alarms" />
                  <LargeNumberIcon number={data.activitiesCount} text="Activities" />
                </div>
              </div>
              <div className="Monitored-Individual-Summary__check-in-area Monitored-Individual-Summary__top-row-item">
                <button
                  className="Monitored-Individual-Summary__check-in-button"
                  onClick={startCheckIn}>Check in</button>
              </div>
            </div>
            {
              data.noteDays &&
              data.noteDays.length > 0 &&
              <div className="Monitored-Individual-Summary__notes">
                <p className="Monitored-Individual-Summary__notes-heading">Recent notes</p>
                <ul className="Monitored-Individual-Summary__daily-notes-list">
                  {
                    data.noteDays.map((note, index) =>
                      <li
                        key={index}
                        className="Monitored-Individual-Summary__daily-notes-item">
                        <button
                          className="Monitored-Individual-Summary__daily-notes-button"
                          onClick={handleDailyNoteIconClick}
                          data-note-index={index}>
                          <DateIcon date={note.noteOn} size="4rem" darkColor={visibleDateIndex === index ? '#363633' : '#8B8B8B'} lightColor="#fdfdfd" />
                        </button>
                      </li>
                    )
                  }
                </ul>
                    {
                      data.noteDays[visibleDateIndex].firstNote &&
                      <React.Fragment>
                        <div className="Monitored-Individual-Summary__visible-note">
                          <div className="Monitored-Individual-Summary__visible-note-info">
                            <label>Note info</label>
                            <div className="Monitored-Individual-Summary__visible-note-info-content">
                              <div className="Monitored-Individual-Summary__visible-note-info-top">
                                <p className="Monitored-Individual-Summary__visible-note-date">{format(parseISO(data.noteDays[visibleDateIndex].firstNote.startOn), 'MMMM d, yyyy')}</p>
                                <p className="Monitored-Individual-Summary__visible-note-dot">{' \u2022 '}</p>
                                <p className="Monitored-Individual-Summary__visible-note-time">{format(parseISO(data.noteDays[visibleDateIndex].firstNote.endOn), 'h:mm aaa')}</p>
                              </div>
                                <p className="Monitored-Individual-Summary__visible-note-author">{`${data.noteDays[visibleDateIndex].firstNote.userLegalFirstName} ${data.noteDays[visibleDateIndex].firstNote.userLastName}`}</p>
                            </div>
                          </div>
                          <div className="Monitored-Individual-Summary__visible-note-content">
                            <label>Comments</label>
                              <p className="Monitored-Individual-Summary__visible-note-content-text">{data.noteDays[visibleDateIndex].firstNote.staffNote}</p>
                          </div>
                        </div>
                      </React.Fragment>
                    }

                <div className="Monitored-Individual-Summary__daily-note-summary">
                  {getDailyNoteSummary()}
                  <Link
                    className="Monitored-Individual-Summary__note-history-link"
                        to={`/monitoredindividual/${selectedMonitoredIndividual.monitoredIndividualId}/notes`}>See full notes history</Link>
                </div>
              </div>
            }
          </div>
        }
        <ModuleLoadingStatus isLoading={dataLoading} isError={dataError} reloadAction={loadData} />
      </div>
      <CardContainer />
      {
        checkInInProgress &&
        //<StaffCheckIn close={endCheckIn} />
        <SurveyModal close={endCheckIn} survey={displayCheckInSurvey} title="Checking in" />
      }
    </div>
  );
}